import { useCallback } from 'react'
import { graphql, useFragment, useMutation } from 'react-relay'

import { type useCriteria_query$data, type useCriteria_query$key } from '@/__generated__/useCriteria_query.graphql'
import { type CriterionPatch, type useCriteriaMutation } from '@/__generated__/useCriteriaMutation.graphql'

type CommitAsyncFn = (patch: CriterionPatch) => Promise<useCriteria_query$data>

export const useCriteria = (
  queryRef?: useCriteria_query$key | null | undefined
): [NonEmpty<useCriteria_query$data['currentUser']>['criterion'], CommitAsyncFn, boolean] => {
  const queryData = useFragment(
    graphql`
      fragment useCriteria_query on Query {
        currentUser {
          criterion {
            ageRange {
              start {
                value
              }
              end {
                value
              }
            }
            isRecentlyActive
            isLocationDealbreaker
            isVerified
            maxDistanceInKm
            maxDistanceInMi
            rowId
          }
        }
      }
    `,
    queryRef
  )
  const [commit, isPending] = useMutation<useCriteriaMutation>(graphql`
    mutation useCriteriaMutation($input: UpdateCriterionInput!) {
      updateCriterion(input: $input) {
        query {
          ...useCriteria_query
        }
      }
    }
  `)

  const commitAsync = useCallback<CommitAsyncFn>(
    (patch: CriterionPatch) =>
      new Promise((resolve, reject) =>
        commit({
          onCompleted: (response, errors) => {
            if (errors) {
              reject(errors)
            } else {
              // @ts-ignore false negative
              resolve(response)
            }
          },
          variables: {
            input: {
              rowId: queryData?.currentUser?.criterion?.rowId,
              patch
            }
          }
        })
      ),
    [commit, queryData?.currentUser?.criterion?.rowId]
  )

  return [queryData?.currentUser?.criterion, commitAsync, isPending]
}
